import React, { useState, useEffect } from 'react';
import { useStoreClubAuth } from '../../contexts/StoreClubAuthContext';
import { animalsAPI } from '../../services/storeClubAPI';
import { toTwoDecimals } from '../../utils/decimal';
import ClubLayout from '../../components/StoreClub/ClubLayout';
import LauraChat from '../../components/StoreClub/LauraChat';

// Helper to safely convert any value to a number
const toNumber = (val: any): number => {
  if (val === null || val === undefined) return 0;
  const num = typeof val === 'number' ? val : parseFloat(String(val));
  return isNaN(num) ? 0 : num;
};

interface Animal {
  id: number;
  category: string;
  animal_type: string;
  animal_type_display: string;
  animal_subtype: string;
  name: string;
  breed: string;
  count: number;
  weight_lbs: number | null;
  activity_level: string;
  life_stage: string;
  dog_size: string;
  housing_type: string;
  production_level: string;
  body_condition: string;
  is_lactating: boolean;
  is_pregnant: boolean;
  is_breeding: boolean;
  is_indoor: boolean | null;
  daily_consumption: number | null;
  monthly_consumption: number | null;
  daily_consumption_lbs: number | null;
}

// Animal category definitions
const ANIMAL_CATEGORIES = [
  { value: 'poultry', label: 'Poultry', icon: '🐔' },
  { value: 'goat', label: 'Goats', icon: '🐐' },
  { value: 'sheep', label: 'Sheep', icon: '🐑' },
  { value: 'swine', label: 'Pigs', icon: '🐷' },
  { value: 'cattle', label: 'Cattle', icon: '🐄' },
  { value: 'horse', label: 'Horses', icon: '🐴' },
  { value: 'rabbit', label: 'Rabbits', icon: '🐰' },
  { value: 'dog', label: 'Dogs', icon: '🐕' },
  { value: 'cat', label: 'Cats', icon: '🐈' },
];

// Subtypes for each category
const POULTRY_SUBTYPES = [
  { value: 'laying_hens', label: 'Laying Hens' },
  { value: 'meat_birds', label: 'Meat Birds (Broilers)' },
  { value: 'pullets', label: 'Pullets' },
  { value: 'chicks', label: 'Chicks' },
  { value: 'mixed_flock', label: 'Mixed Flock' },
  { value: 'ducks', label: 'Ducks' },
  { value: 'turkeys', label: 'Turkeys' },
];

const GOAT_SUBTYPES = [
  { value: 'dairy', label: 'Dairy Goat' },
  { value: 'meat', label: 'Meat Goat (Boer)' },
  { value: 'fiber', label: 'Fiber Goat' },
  { value: 'pet', label: 'Pet/Companion' },
];

const SHEEP_SUBTYPES = [
  { value: 'meat_sheep', label: 'Meat Sheep' },
  { value: 'wool', label: 'Wool Sheep' },
  { value: 'dairy_sheep', label: 'Dairy Sheep' },
  { value: 'hair', label: 'Hair Sheep' },
];

const SWINE_SUBTYPES = [
  { value: 'feeder', label: 'Feeder Pigs' },
  { value: 'breeding_sow', label: 'Breeding Sow' },
  { value: 'boar', label: 'Boar' },
  { value: 'pet_pig', label: 'Pet Pig (Kunekune, etc.)' },
];

const CATTLE_SUBTYPES = [
  { value: 'beef', label: 'Beef Cattle' },
  { value: 'dairy_cow', label: 'Dairy Cattle' },
  { value: 'dual_purpose', label: 'Dual Purpose' },
];

const HORSE_SUBTYPES = [
  { value: 'light_riding', label: 'Light Riding' },
  { value: 'draft', label: 'Draft Horse' },
  { value: 'pony', label: 'Pony' },
  { value: 'mini', label: 'Miniature' },
];

const RABBIT_SUBTYPES = [
  { value: 'meat_rabbit', label: 'Meat Rabbit' },
  { value: 'fiber_rabbit', label: 'Fiber (Angora)' },
  { value: 'pet_rabbit', label: 'Pet' },
];

const DOG_SIZES = [
  { value: 'toy', label: 'Toy', desc: 'under 12 lbs' },
  { value: 'small', label: 'Small', desc: '12-25 lbs' },
  { value: 'medium', label: 'Medium', desc: '25-50 lbs' },
  { value: 'large', label: 'Large', desc: '50-100 lbs' },
  { value: 'giant', label: 'Giant', desc: '100+ lbs' },
];

const LIFE_STAGES_BY_CATEGORY: Record<string, Array<{ value: string; label: string }>> = {
  dog: [
    { value: 'puppy', label: 'Puppy' },
    { value: 'adult', label: 'Adult' },
    { value: 'senior', label: 'Senior' },
  ],
  cat: [
    { value: 'kitten', label: 'Kitten' },
    { value: 'adult', label: 'Adult' },
    { value: 'senior', label: 'Senior' },
  ],
  poultry: [
    { value: 'chick', label: 'Chicks (0-8 weeks)' },
    { value: 'grower', label: 'Grower (8-20 weeks)' },
    { value: 'laying', label: 'Laying' },
    { value: 'finishing', label: 'Finishing (meat)' },
  ],
  goat: [
    { value: 'kid', label: 'Kid' },
    { value: 'yearling', label: 'Yearling' },
    { value: 'adult', label: 'Adult' },
  ],
  sheep: [
    { value: 'kid', label: 'Lamb' },
    { value: 'yearling', label: 'Yearling' },
    { value: 'adult', label: 'Adult' },
  ],
  swine: [
    { value: 'piglet', label: 'Piglet' },
    { value: 'grower_pig', label: 'Grower (50-125 lbs)' },
    { value: 'finisher', label: 'Finisher (125-275 lbs)' },
    { value: 'breeding', label: 'Breeding Stock' },
  ],
  cattle: [
    { value: 'kid', label: 'Calf' },
    { value: 'yearling', label: 'Yearling' },
    { value: 'adult', label: 'Adult' },
  ],
  horse: [
    { value: 'foal', label: 'Foal' },
    { value: 'yearling', label: 'Yearling' },
    { value: 'adult', label: 'Adult' },
    { value: 'senior', label: 'Senior' },
  ],
  rabbit: [
    { value: 'kit', label: 'Kit' },
    { value: 'junior', label: 'Junior' },
    { value: 'adult', label: 'Adult' },
  ],
};

const ACTIVITY_LEVELS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'working', label: 'Working' },
];

const BODY_CONDITIONS = [
  { value: 'thin', label: 'Thin' },
  { value: 'ideal', label: 'Ideal' },
  { value: 'overweight', label: 'Overweight' },
];

const HOUSING_TYPES = [
  { value: 'free_range', label: 'Free Range' },
  { value: 'pastured', label: 'Pastured' },
  { value: 'confined', label: 'Confined/Coop' },
];

const PRODUCTION_LEVELS = [
  { value: 'high', label: 'High/Commercial' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'low', label: 'Low/Pet' },
];

// Feed calculation functions
// Rounded to the DecimalField's two places BEFORE it goes anywhere: 0.15 * 6 is
// 0.8999999999999999 in JavaScript, and DRF rejects seventeen digits into a
// DecimalField(max_digits=6, decimal_places=2) - which is what blocked Tom's signup
// six times over. See utils/decimal.ts.
const calculateDailyFeed = (data: FormData): number => toTwoDecimals(rawDailyFeed(data));

const rawDailyFeed = (data: FormData): number => {
  const { category, animal_subtype, life_stage, count, weight_lbs, is_lactating, is_pregnant, is_breeding, production_level, activity_level } = data;
  const weight = parseFloat(weight_lbs) || 0;
  const qty = count || 1;

  switch (category) {
    case 'poultry':
      switch (life_stage) {
        case 'chick': return 0.05 * qty;
        case 'grower': return 0.15 * qty;
        case 'laying':
          if (production_level === 'high') return 0.30 * qty;
          if (production_level === 'moderate') return 0.27 * qty;
          return 0.25 * qty;
        case 'finishing': return 0.45 * qty;
        default: return 0.25 * qty;
      }

    case 'goat':
      let goatBase = weight * 0.035; // 3.5% body weight
      if (life_stage === 'kid') goatBase = 1.5;
      if (is_lactating) goatBase += 1.0;
      if (is_pregnant) goatBase *= 1.15;
      return goatBase * qty;

    case 'sheep':
      let sheepBase = weight * 0.035;
      if (life_stage === 'kid') sheepBase = 1.5;
      if (is_lactating) sheepBase += 0.75;
      if (is_pregnant) sheepBase *= 1.15;
      return sheepBase * qty;

    case 'swine':
      switch (life_stage) {
        case 'piglet': return 1.5 * qty;
        case 'grower_pig': return 5.0 * qty;
        case 'finisher': return 7.0 * qty;
        case 'breeding':
          if (is_lactating) return 12.0 * qty;
          if (is_pregnant) return 6.0 * qty;
          return 5.0 * qty;
        default: return 5.0 * qty;
      }

    case 'cattle':
      let cattleBase = weight * 0.025; // 2.5% body weight
      if (life_stage === 'kid') cattleBase = 8.0;
      if (animal_subtype === 'dairy_cow' && is_lactating) cattleBase *= 1.5;
      return cattleBase * qty;

    case 'horse':
      let horseBase = weight * 0.02; // 2% body weight
      if (activity_level === 'working') horseBase *= 1.25;
      if (activity_level === 'high') horseBase *= 1.15;
      return horseBase * qty;

    case 'rabbit':
      let rabbitBase = 0.15;
      if (is_lactating) rabbitBase = 0.30;
      if (is_breeding) rabbitBase = 0.20;
      return rabbitBase * qty;

    case 'dog':
      // Weight-based for dogs (cups per day)
      const dogCups = calculateDogCups(weight, activity_level, life_stage);
      return dogCups;

    case 'cat':
      // Weight-based for cats (cups per day)
      const catCups = calculateCatCups(weight, activity_level, life_stage);
      return catCups;

    default:
      return 0;
  }
};

const calculateDogCups = (weight: number, activity: string, stage: string): number => {
  // Base calculation: calories needed / calories per cup
  let baseCups = 0;
  if (weight <= 12) baseCups = weight * 0.05;
  else if (weight <= 25) baseCups = 0.6 + (weight - 12) * 0.04;
  else if (weight <= 50) baseCups = 1.1 + (weight - 25) * 0.035;
  else if (weight <= 100) baseCups = 2.0 + (weight - 50) * 0.03;
  else baseCups = 3.5 + (weight - 100) * 0.025;

  // Adjust for activity
  if (activity === 'high' || activity === 'working') baseCups *= 1.3;
  if (activity === 'low') baseCups *= 0.85;

  // Adjust for life stage
  if (stage === 'puppy') baseCups *= 1.5;
  if (stage === 'senior') baseCups *= 0.9;

  return Math.round(baseCups * 10) / 10;
};

const calculateCatCups = (weight: number, activity: string, stage: string): number => {
  // Cats typically need 0.25-0.5 cups per 5 lbs
  let baseCups = (weight / 5) * 0.35;

  if (activity === 'high') baseCups *= 1.2;
  if (activity === 'low') baseCups *= 0.85;
  if (stage === 'kitten') baseCups *= 1.5;
  if (stage === 'senior') baseCups *= 0.9;

  return Math.round(baseCups * 10) / 10;
};

const getRecommendedFeed = (category: string, subtype: string, lifeStage: string): string => {
  switch (category) {
    case 'poultry':
      if (lifeStage === 'chick') return 'Chick Starter';
      if (lifeStage === 'grower') return 'Grower Feed';
      if (lifeStage === 'laying') return 'Layer Feed';
      if (lifeStage === 'finishing') return 'Meat Bird Feed';
      return 'All Flock Feed';
    case 'goat':
      if (subtype === 'dairy') return 'Dairy Goat Chow';
      if (lifeStage === 'kid') return 'Kid Starter';
      return 'Goat Chow';
    case 'swine':
      if (lifeStage === 'piglet') return 'Pig Starter';
      if (lifeStage === 'grower_pig') return 'Pig Grower';
      if (lifeStage === 'finisher') return 'Pig Finisher';
      return 'Sow Feed';
    case 'horse':
      return 'Cavalry Horse Mash';
    default:
      return '';
  }
};

interface FormData {
  category: string;
  animal_subtype: string;
  name: string;
  breed: string;
  count: number;
  weight_lbs: string;
  life_stage: string;
  activity_level: string;
  body_condition: string;
  housing_type: string;
  production_level: string;
  is_lactating: boolean;
  is_pregnant: boolean;
  is_breeding: boolean;
  is_indoor: boolean | null;
  dog_size: string;
}

const initialFormData: FormData = {
  category: '',
  animal_subtype: '',
  name: '',
  breed: '',
  count: 1,
  weight_lbs: '',
  life_stage: '',
  activity_level: 'normal',
  body_condition: 'ideal',
  housing_type: '',
  production_level: '',
  is_lactating: false,
  is_pregnant: false,
  is_breeding: false,
  is_indoor: null,
  dog_size: '',
};

const ClubFarm: React.FC = () => {
  const { member, refreshProfile } = useStoreClubAuth();
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState<Animal | null>(null);
  const [saving, setSaving] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [calculatedFeed, setCalculatedFeed] = useState<number>(0);

  useEffect(() => {
    loadAnimals();
  }, []);

  useEffect(() => {
    // Recalculate feed whenever form data changes
    if (formData.category) {
      try {
        const feed = calculateDailyFeed(formData);
        setCalculatedFeed(feed);
      } catch (e) {
        console.error('[ClubFarm] Error calculating feed:', e);
        setCalculatedFeed(0);
      }
    }
  }, [formData]);

  const loadAnimals = async () => {
    setError(null);
    try {
      console.log('[ClubFarm] Loading animals...');
      const data = await animalsAPI.getAll();
      console.log('[ClubFarm] Loaded animals:', data?.length || 0);
      setAnimals(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('[ClubFarm] Failed to load animals:', err);
      setError(err?.message || 'Failed to load animals');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setFormStep(1);
    setCalculatedFeed(0);
    setEditingAnimal(null);
    setShowAddForm(false);
  };

  const handleEditAnimal = (animal: Animal) => {
    setFormData({
      category: animal.category,
      animal_subtype: animal.animal_subtype || '',
      name: animal.name || '',
      breed: animal.breed || '',
      count: animal.count || 1,
      weight_lbs: animal.weight_lbs?.toString() || '',
      life_stage: animal.life_stage || '',
      activity_level: animal.activity_level || 'normal',
      body_condition: animal.body_condition || 'ideal',
      housing_type: animal.housing_type || '',
      production_level: animal.production_level || '',
      is_lactating: animal.is_lactating || false,
      is_pregnant: animal.is_pregnant || false,
      is_breeding: animal.is_breeding || false,
      is_indoor: animal.is_indoor,
      dog_size: animal.dog_size || '',
    });
    setEditingAnimal(animal);
    setFormStep(2); // Skip category selection when editing
    setShowAddForm(true);
  };

  const handleSaveAnimal = async () => {
    setSaving(true);
    try {
      const isPet = ['dog', 'cat'].includes(formData.category);
      const dailyFeed = calculateDailyFeed(formData);

      const data: any = {
        category: formData.category,
        animal_type: formData.category,
        animal_subtype: formData.animal_subtype,
        name: formData.name || undefined,
        breed: formData.breed || undefined,
        count: isPet ? 1 : formData.count,
        weight_lbs: formData.weight_lbs ? parseFloat(formData.weight_lbs) : undefined,
        life_stage: formData.life_stage,
        activity_level: formData.activity_level,
        body_condition: formData.body_condition,
        housing_type: formData.housing_type,
        production_level: formData.production_level,
        is_lactating: formData.is_lactating,
        is_pregnant: formData.is_pregnant,
        is_breeding: formData.is_breeding,
        is_indoor: formData.is_indoor,
        dog_size: formData.dog_size,
      };

      // Set consumption
      if (isPet) {
        data.daily_consumption_cups = dailyFeed;
      } else {
        data.daily_consumption_lbs = dailyFeed;
      }

      if (editingAnimal) {
        await animalsAPI.update(editingAnimal.id, data);
      } else {
        await animalsAPI.create(data);
      }

      await loadAnimals();
      try {
        await refreshProfile();
      } catch (e) {
        console.warn('[ClubFarm] Profile refresh failed, continuing:', e);
      }
      resetForm();
    } catch (error) {
      console.error('Failed to save animal:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAnimal = async (id: number) => {
    if (!window.confirm('Remove this animal from your farm?')) return;
    try {
      await animalsAPI.delete(id);
      await loadAnimals();
      try {
        await refreshProfile();
      } catch (e) {
        console.warn('[ClubFarm] Profile refresh failed:', e);
      }
    } catch (error) {
      console.error('Failed to delete animal:', error);
    }
  };

  const getSubtypes = () => {
    switch (formData.category) {
      case 'poultry': return POULTRY_SUBTYPES;
      case 'goat': return GOAT_SUBTYPES;
      case 'sheep': return SHEEP_SUBTYPES;
      case 'swine': return SWINE_SUBTYPES;
      case 'cattle': return CATTLE_SUBTYPES;
      case 'horse': return HORSE_SUBTYPES;
      case 'rabbit': return RABBIT_SUBTYPES;
      default: return [];
    }
  };

  const getLifeStages = () => {
    return LIFE_STAGES_BY_CATEGORY[formData.category] || [];
  };

  const isPet = ['dog', 'cat'].includes(formData.category);
  const needsWeight = ['dog', 'cat', 'goat', 'sheep', 'cattle', 'horse'].includes(formData.category);
  const needsCount = !isPet;
  const needsHousing = ['poultry'].includes(formData.category);
  const needsProduction = ['poultry', 'goat', 'sheep', 'cattle'].includes(formData.category) &&
    (formData.life_stage === 'laying' || formData.life_stage === 'adult');
  const canBeLactating = ['goat', 'sheep', 'cattle', 'swine', 'rabbit'].includes(formData.category) &&
    (formData.life_stage === 'adult' || formData.life_stage === 'breeding');
  const canBePregnant = canBeLactating;

  const getAnimalIcon = (cat: string) => {
    return ANIMAL_CATEGORIES.find(c => c.value === cat)?.icon || '🐾';
  };

  const getLauraMessage = () => {
    if (animals.length === 0) {
      return "Let's add your animals! Tell me about your flock, herd, or pets so I can help you with feeding recommendations.";
    }
    const totalDaily = animals.reduce((sum, a) => {
      return sum + toNumber(a.daily_consumption_lbs || a.daily_consumption);
    }, 0);
    return `You have ${animals.length} animal${animals.length === 1 ? '' : ' group(s)'} on your farm. About ${toNumber(totalDaily).toFixed(1)} lbs/day of feed.`;
  };

  if (loading) {
    return (
      <ClubLayout>
        <div style={styles.loading}>Loading your farm...</div>
      </ClubLayout>
    );
  }

  if (error) {
    return (
      <ClubLayout>
        <LauraChat message="I'm having trouble loading your farm data. Please try refreshing the page." />
        <div style={styles.content}>
          <div style={styles.errorState}>
            <p style={styles.errorText}>{error}</p>
            <button style={styles.retryButton} onClick={() => { setLoading(true); loadAnimals(); }}>
              Try Again
            </button>
          </div>
        </div>
      </ClubLayout>
    );
  }

  // Form Step Rendering
  const renderFormStep = () => {
    // Step 1: Category Selection
    if (formStep === 1) {
      return (
        <div>
          <h3 style={styles.formTitle}>What kind of animal?</h3>
          <div style={styles.categoryGrid}>
            {ANIMAL_CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                style={{
                  ...styles.categoryButton,
                  backgroundColor: formData.category === cat.value ? '#508590' : '#fff',
                  color: formData.category === cat.value ? '#fff' : '#333',
                  borderColor: formData.category === cat.value ? '#508590' : '#e0e0e0',
                }}
                onClick={() => {
                  setFormData({ ...initialFormData, category: cat.value });
                  setFormStep(2);
                }}
              >
                <span style={styles.categoryIcon}>{cat.icon}</span>
                <span style={styles.categoryLabel}>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Step 2+: Category-specific questions
    const subtypes = getSubtypes();
    const lifeStages = getLifeStages();

    return (
      <div>
        <div style={styles.formHeader}>
          <button style={styles.backButton} onClick={() => editingAnimal ? resetForm() : setFormStep(1)}>
            ← Back
          </button>
          <span style={styles.categoryBadge}>
            {getAnimalIcon(formData.category)} {ANIMAL_CATEGORIES.find(c => c.value === formData.category)?.label}
          </span>
        </div>

        {/* Subtype Selection (if applicable) */}
        {subtypes.length > 0 && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Type</label>
            <div style={styles.optionGrid}>
              {subtypes.map((st) => (
                <button
                  key={st.value}
                  style={{
                    ...styles.optionButton,
                    backgroundColor: formData.animal_subtype === st.value ? '#508590' : '#fff',
                    color: formData.animal_subtype === st.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, animal_subtype: st.value })}
                >
                  {st.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Dog size selection */}
        {formData.category === 'dog' && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Size Category</label>
            <div style={styles.optionGrid}>
              {DOG_SIZES.map((sz) => (
                <button
                  key={sz.value}
                  style={{
                    ...styles.optionButton,
                    backgroundColor: formData.dog_size === sz.value ? '#508590' : '#fff',
                    color: formData.dog_size === sz.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, dog_size: sz.value })}
                >
                  <span>{sz.label}</span>
                  <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>{sz.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Count (for livestock) */}
        {needsCount && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>How many?</label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.count}
              onChange={(e) => setFormData({ ...formData, count: e.target.value as any })}
              onBlur={(e) => {
                const val = parseInt(e.target.value) || 1;
                setFormData({ ...formData, count: Math.max(1, Math.min(10000, val)) });
              }}
              style={styles.input}
            />
          </div>
        )}

        {/* Name (for pets) */}
        {isPet && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Name (optional)</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={styles.input}
              placeholder="Buddy"
            />
          </div>
        )}

        {/* Life Stage */}
        {lifeStages.length > 0 && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Life Stage</label>
            <div style={styles.optionGrid}>
              {lifeStages.map((st) => (
                <button
                  key={st.value}
                  style={{
                    ...styles.optionButton,
                    backgroundColor: formData.life_stage === st.value ? '#508590' : '#fff',
                    color: formData.life_stage === st.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, life_stage: st.value })}
                >
                  {st.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Weight */}
        {needsWeight && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              {isPet ? 'Weight (lbs)' : 'Average Weight (lbs)'}
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={formData.weight_lbs}
              onChange={(e) => setFormData({ ...formData, weight_lbs: e.target.value })}
              onBlur={(e) => {
                const val = parseFloat(e.target.value) || 0;
                if (val > 0) {
                  setFormData({ ...formData, weight_lbs: Math.min(2500, val).toString() });
                }
              }}
              style={styles.input}
              placeholder={isPet ? '50' : '150'}
            />
          </div>
        )}

        {/* Activity Level */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Activity Level</label>
          <div style={styles.optionRow}>
            {ACTIVITY_LEVELS.filter(a => formData.category === 'horse' || a.value !== 'working').map((lvl) => (
              <button
                key={lvl.value}
                style={{
                  ...styles.optionButtonSmall,
                  backgroundColor: formData.activity_level === lvl.value ? '#508590' : '#fff',
                  color: formData.activity_level === lvl.value ? '#fff' : '#333',
                }}
                onClick={() => setFormData({ ...formData, activity_level: lvl.value })}
              >
                {lvl.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body Condition (for pets and large animals) */}
        {(isPet || needsWeight) && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Body Condition</label>
            <div style={styles.optionRow}>
              {BODY_CONDITIONS.map((bc) => (
                <button
                  key={bc.value}
                  style={{
                    ...styles.optionButtonSmall,
                    backgroundColor: formData.body_condition === bc.value ? '#508590' : '#fff',
                    color: formData.body_condition === bc.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, body_condition: bc.value })}
                >
                  {bc.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Housing (for poultry) */}
        {needsHousing && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Housing Type</label>
            <div style={styles.optionRow}>
              {HOUSING_TYPES.map((h) => (
                <button
                  key={h.value}
                  style={{
                    ...styles.optionButtonSmall,
                    backgroundColor: formData.housing_type === h.value ? '#508590' : '#fff',
                    color: formData.housing_type === h.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, housing_type: h.value })}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Production Level (for layers, dairy) */}
        {needsProduction && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Production Level</label>
            <div style={styles.optionRow}>
              {PRODUCTION_LEVELS.map((p) => (
                <button
                  key={p.value}
                  style={{
                    ...styles.optionButtonSmall,
                    backgroundColor: formData.production_level === p.value ? '#508590' : '#fff',
                    color: formData.production_level === p.value ? '#fff' : '#333',
                  }}
                  onClick={() => setFormData({ ...formData, production_level: p.value })}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Lactating / Pregnant (for appropriate animals) */}
        {canBeLactating && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Status</label>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={formData.is_lactating}
                  onChange={(e) => setFormData({ ...formData, is_lactating: e.target.checked })}
                />
                Lactating
              </label>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={formData.is_pregnant}
                  onChange={(e) => setFormData({ ...formData, is_pregnant: e.target.checked })}
                />
                Pregnant
              </label>
              {formData.category === 'rabbit' && (
                <label style={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={formData.is_breeding}
                    onChange={(e) => setFormData({ ...formData, is_breeding: e.target.checked })}
                  />
                  Breeding
                </label>
              )}
            </div>
          </div>
        )}

        {/* Indoor/Outdoor for cats */}
        {formData.category === 'cat' && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>Indoor/Outdoor</label>
            <div style={styles.optionRow}>
              <button
                style={{
                  ...styles.optionButtonSmall,
                  backgroundColor: formData.is_indoor === true ? '#508590' : '#fff',
                  color: formData.is_indoor === true ? '#fff' : '#333',
                }}
                onClick={() => setFormData({ ...formData, is_indoor: true })}
              >
                Indoor Only
              </button>
              <button
                style={{
                  ...styles.optionButtonSmall,
                  backgroundColor: formData.is_indoor === false ? '#508590' : '#fff',
                  color: formData.is_indoor === false ? '#fff' : '#333',
                }}
                onClick={() => setFormData({ ...formData, is_indoor: false })}
              >
                Indoor/Outdoor
              </button>
            </div>
          </div>
        )}

        {/* Feed Calculation Result */}
        {calculatedFeed > 0 && (
          <div style={styles.feedCalc}>
            <div style={styles.feedCalcHeader}>Estimated Daily Feed</div>
            <div style={styles.feedCalcValue}>
              {toNumber(calculatedFeed).toFixed(1)} {isPet ? 'cups' : 'lbs'}/day
            </div>
            <div style={styles.feedCalcMonthly}>
              ~{(toNumber(calculatedFeed) * 30).toFixed(0)} {isPet ? 'cups' : 'lbs'}/month
            </div>
            {!isPet && getRecommendedFeed(formData.category, formData.animal_subtype, formData.life_stage) && (
              <div style={styles.feedRecommend}>
                Recommended: {getRecommendedFeed(formData.category, formData.animal_subtype, formData.life_stage)}
              </div>
            )}
          </div>
        )}

        {/* Save/Cancel Buttons */}
        <div style={styles.formActions}>
          <button style={styles.cancelButton} onClick={resetForm}>
            Cancel
          </button>
          <button
            style={{
              ...styles.saveButton,
              opacity: saving || !formData.life_stage ? 0.6 : 1,
            }}
            onClick={handleSaveAnimal}
            disabled={saving || !formData.life_stage}
          >
            {saving ? 'Saving...' : (editingAnimal ? 'Save Changes' : 'Add Animal')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <ClubLayout>
      <LauraChat message={getLauraMessage()} />

      <div style={styles.content}>
        <div style={styles.header}>
          <h2 style={styles.heading}>My Farm</h2>
          {!showAddForm && (
            <button style={styles.addButton} onClick={() => setShowAddForm(true)}>
              + Add Animal
            </button>
          )}
        </div>

        {showAddForm && (
          <div style={styles.form}>
            {renderFormStep()}
          </div>
        )}

        {!showAddForm && animals.length === 0 && (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>🐾</div>
            <p style={styles.emptyText}>No animals yet</p>
            <button style={styles.addFirstButton} onClick={() => setShowAddForm(true)}>
              Add Your First Animal
            </button>
          </div>
        )}

        {!showAddForm && animals.length > 0 && (
          <div style={styles.animalList}>
            {animals.map((animal) => {
              const isAnimalPet = ['dog', 'cat'].includes(animal.category);
              return (
                <div key={animal.id} style={styles.animalCard}>
                  <div style={styles.animalIcon}>{getAnimalIcon(animal.category)}</div>
                  <div style={styles.animalInfo}>
                    <span style={styles.animalName}>
                      {animal.name || animal.animal_type_display}
                    </span>
                    <span style={styles.animalDetails}>
                      {isAnimalPet ? (
                        `${animal.weight_lbs || '?'} lbs • ${animal.life_stage || 'adult'}`
                      ) : (
                        `${animal.count || 1} ${animal.animal_subtype || animal.category}`
                      )}
                    </span>
                    {(animal.daily_consumption_lbs || animal.daily_consumption) != null && (
                      <span style={styles.animalConsumption}>
                        ~{toNumber(animal.daily_consumption_lbs || animal.daily_consumption).toFixed(1)} {isAnimalPet ? 'cups' : 'lbs'}/day
                      </span>
                    )}
                  </div>
                  <div style={styles.animalActions}>
                    <button style={styles.editButton} onClick={() => handleEditAnimal(animal)}>
                      Edit
                    </button>
                    <button style={styles.deleteButton} onClick={() => handleDeleteAnimal(animal.id)}>
                      ×
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ClubLayout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  content: {
    padding: '1rem',
    paddingBottom: '5rem',
    maxWidth: '600px',
    margin: '0 auto',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#666',
  },
  errorState: {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: '#ffebee',
    borderRadius: '12px',
    marginTop: '1rem',
  },
  errorText: {
    color: '#c62828',
    fontSize: '1rem',
    margin: '0 0 1rem 0',
  },
  retryButton: {
    backgroundColor: '#508590',
    color: '#fff',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  addButton: {
    backgroundColor: '#508590',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  form: {
    backgroundColor: '#fff',
    padding: '1.5rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '1.5rem',
  },
  formHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: '#508590',
    fontSize: '0.9375rem',
    cursor: 'pointer',
    padding: 0,
  },
  categoryBadge: {
    backgroundColor: '#f0f7f8',
    color: '#508590',
    padding: '0.375rem 0.75rem',
    borderRadius: '16px',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  formTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 1rem 0',
    textAlign: 'center',
  },
  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.75rem',
  },
  categoryButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '1rem 0.5rem',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#fff',
  },
  categoryIcon: {
    fontSize: '1.75rem',
  },
  categoryLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  inputGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#333',
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  optionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '0.5rem',
  },
  optionRow: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  optionButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.75rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#fff',
  },
  optionButtonSmall: {
    flex: 1,
    padding: '0.625rem 0.5rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#fff',
    minWidth: '60px',
  },
  checkboxGroup: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9375rem',
    cursor: 'pointer',
  },
  feedCalc: {
    backgroundColor: '#e8f5e9',
    padding: '1rem',
    borderRadius: '12px',
    textAlign: 'center',
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  feedCalcHeader: {
    fontSize: '0.75rem',
    color: '#388e3c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.25rem',
  },
  feedCalcValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2e7d32',
  },
  feedCalcMonthly: {
    fontSize: '0.875rem',
    color: '#388e3c',
    marginTop: '0.25rem',
  },
  feedRecommend: {
    fontSize: '0.8125rem',
    color: '#508590',
    marginTop: '0.5rem',
    fontWeight: 500,
  },
  formActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.5rem',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: 'transparent',
    color: '#666',
    border: '2px solid #e0e0e0',
    padding: '0.875rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  saveButton: {
    flex: 2,
    backgroundColor: '#508590',
    color: '#fff',
    border: 'none',
    padding: '0.875rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '3rem',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  emptyText: {
    color: '#666',
    margin: '0 0 1rem 0',
  },
  addFirstButton: {
    backgroundColor: '#508590',
    color: '#fff',
    border: 'none',
    padding: '0.875rem 1.5rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  animalList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  animalCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  animalIcon: {
    fontSize: '2rem',
  },
  animalInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  animalName: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1rem',
  },
  animalDetails: {
    fontSize: '0.8125rem',
    color: '#666',
  },
  animalConsumption: {
    fontSize: '0.75rem',
    color: '#508590',
    fontWeight: 500,
  },
  animalActions: {
    display: 'flex',
    gap: '0.5rem',
  },
  editButton: {
    backgroundColor: 'transparent',
    color: '#508590',
    border: '1px solid #508590',
    padding: '0.375rem 0.75rem',
    borderRadius: '6px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  deleteButton: {
    width: '28px',
    height: '28px',
    backgroundColor: '#ffebee',
    color: '#E73C3E',
    border: 'none',
    borderRadius: '50%',
    fontSize: '1.25rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

export default ClubFarm;
